import { Button, Grid, Paper, TextField, Typography } from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { UpdateRounded } from '@material-ui/icons';
import { unwrapResult } from '@reduxjs/toolkit';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { RootState } from '../../app/store';
import { useAppDispatch } from '../../hooks/useAppRudux';
import Breadcrumb from '../Breadcrumb';
import loadingSlice from '../Loading/loadingSlice';
import {
  ApprovalPeriod,
  getProjectApprovalPeriodsAsync,
  updateProjectApprovalPeriodAsync
} from '../ProjectApprovalPeriod/projectApprovalPeriodSlice';
import { getProjectAsync } from './projectSlice';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    paper: {
      borderRadius: 16,
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2)
    },
    positionText: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(0, 2)
    },
    numberField: {
      width: 80,
      paddingRight: 16
    },
    marginBottom: {
      marginBottom: theme.spacing(2),
      '&:last-child': {
        marginBottom: 0
      }
    },
    flexGrow: {
      flexGrow: 1
    }
  })
);

export default function ProjectEdit() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { data: workflowSteps } = useSelector(
    (state: RootState) => state.workflowSteps
  );
  const { data: projectApprovalPeriods } = useSelector(
    (state: RootState) => state.projectApprovalPeriods
  );
  const { selectedProject } = useSelector((state: RootState) => state.projects);

  const params = useParams<{ projectId: string }>();
  const projectId = Number.parseInt(params.projectId);

  const [approvalPeriods, setApprovalPeriods] = useState<ApprovalPeriod[]>([]);

  useEffect(() => {
    dispatch(getProjectAsync(projectId));
    dispatch(getProjectApprovalPeriodsAsync(projectId));
  }, [workflowSteps]);

  useEffect(() => {
    const approvalPeriods = workflowSteps.map((workflowStep) => {
      const projectApprovalPeriod = projectApprovalPeriods.find(
        (projectApprovalPeriod: ApprovalPeriod) =>
          workflowStep.id === projectApprovalPeriod.workflowStepId
      );
      return {
        workflowStepId: workflowStep.id,
        value: projectApprovalPeriod ? projectApprovalPeriod.value : null
      };
    });
    setApprovalPeriods(approvalPeriods);
  }, [projectApprovalPeriods]);

  const [loading, setLoading] = useState<boolean>(false);

  async function onSubmit(
    event: React.FormEvent<HTMLFormElement>,
    projectId: number,
    approvalPeriods: ApprovalPeriod[]
  ) {
    event.preventDefault();
    try {
      setLoading(true);
      const result = await dispatch(
        updateProjectApprovalPeriodAsync({
          projectId,
          approvalPeriods: approvalPeriods.filter(
            (approvalPeriod) => approvalPeriod.value !== null
          )
        })
      );
      await unwrapResult(result);
      dispatch(
        loadingSlice.actions.success({
          message: t('messages.success.projectApprovalPeriodUpdated')
        })
      );
      setTimeout(() => {
        history.push(`/projects/${projectId}?tabId=2`);
      }, 1000);
    } catch (error) {
      setLoading(false);
    }
  }

  function onChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;
    const workflowStepId = Number.parseInt(name, 10);
    const newValue = Number.parseInt(value, 10);
    const newApprovalPeriods = approvalPeriods.filter(
      (approvalPeriod) => approvalPeriod.workflowStepId !== workflowStepId
    );
    newApprovalPeriods.push({
      workflowStepId,
      value: value === '' || newValue <= 0 ? null : newValue
    });
    setApprovalPeriods(newApprovalPeriods);
  }

  return (
    <div className={classes.root}>
      <Breadcrumb
        name="editProject"
        data={{
          projectName: selectedProject?.name,
          projectId: selectedProject?.id
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {t('project.edit.title')}
      </Typography>
      <form onSubmit={(event) => onSubmit(event, projectId, approvalPeriods)}>
        <Paper className={classes.paper}>
          <Grid container spacing={3} justify="flex-start">
            <Grid item xs={12} md={6}>
              {workflowSteps.map((workflowStep) => {
                const approvalPeriod = approvalPeriods.find(
                  (approvalPeriod) =>
                    approvalPeriod.workflowStepId === workflowStep.id
                );
                return (
                  <div
                    className={clsx(classes.marginBottom, classes.positionText)}
                    key={workflowStep.name}
                  >
                    <Typography className={classes.flexGrow}>
                      {workflowStep.name}
                    </Typography>
                    <TextField
                      className={classes.numberField}
                      name={`${workflowStep.id}`}
                      type="number"
                      variant="outlined"
                      size="small"
                      value={approvalPeriod?.value ? approvalPeriod?.value : ''}
                      onChange={onChange}
                    />
                    <Typography>{t('project.edit.days')}</Typography>
                  </div>
                );
              })}
            </Grid>
          </Grid>
        </Paper>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          disabled={loading}
          startIcon={<UpdateRounded />}
        >
          {t('project.edit.updateButton')}
        </Button>
      </form>
    </div>
  );
}
